import { useNavigate } from 'react-router'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TaskCard, type CardWhere } from '@/components/TaskCard'
import type { People } from '@/hooks/usePeople'
import { useTaskActions } from '@/hooks/useTaskActions'
import type { PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useCategories } from '@/store/categories'
import { toggleSelected, useUi } from '@/store/ui'

export interface CardContext {
  now: Now
  people: People
  /** A past week: nothing drags, tasks open read-only, and only a done row's ring works, to untick. */
  readOnly: boolean
}

interface PlanCardProps {
  item: PlanItem
  where: CardWhere
  ctx: CardContext
  /** The list it sits in, for drops: a day's date or a tray group. */
  container: string
  showDoneDay?: boolean
}

/** A task in a day or a tray group. Real open cards drag; ticked rows and previews don't. */
export function PlanCard({ item, where, ctx, container, showDoneDay }: PlanCardProps) {
  const selecting = useUi((s) => s.selecting)
  const actable = !ctx.readOnly && !item.done && !item.faint
  // in Select mode nothing drags: a tap picks the card instead
  return actable && !selecting ? <SortableCard item={item} where={where} ctx={ctx} container={container} /> : <StaticCard item={item} where={where} ctx={ctx} showDoneDay={showDoneDay} selectable={actable && selecting} />
}

function useCardProps(item: PlanItem, where: CardWhere, ctx: CardContext) {
  const navigate = useNavigate()
  const actions = useTaskActions()
  const category = useCategories((s) => (item.task.categoryId ? (s.byId[item.task.categoryId] ?? null) : null))
  const t = item.task
  const readOnly = ctx.readOnly || item.faint
  return {
    item,
    where,
    now: ctx.now,
    people: ctx.people,
    category,
    readOnly: ctx.readOnly,
    onOpen: () => navigate(`/task/${t.id}`, { state: readOnly ? { readOnly: true } : undefined }),
    onToggleDone: () => actions.toggleDone(t, item.completion),
    onToggleStep: (stepId: string, done: boolean) => actions.toggleStep(t, stepId, done),
  }
}

function StaticCard({ item, where, ctx, showDoneDay, selectable = false }: { item: PlanItem; where: CardWhere; ctx: CardContext; showDoneDay?: boolean; selectable?: boolean }) {
  const props = useCardProps(item, where, ctx)
  const selected = useUi((s) => s.selected.includes(item.task.id))
  return (
    <div className="plist__row">
      <TaskCard {...props} showDoneDay={showDoneDay} selection={selectable ? { selected, onToggle: () => toggleSelected(item.task.id) } : undefined} />
    </div>
  )
}

function SortableCard({ item, where, ctx, container }: { item: PlanItem; where: CardWhere; ctx: CardContext; container: string }) {
  const props = useCardProps(item, where, ctx)
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.task.id, data: { container } })
  const style = { transform: CSS.Translate.toString(transform), transition }
  return (
    <div ref={setNodeRef} style={style} className={`plist__row plist__row--drag ${isDragging ? 'plist__row--lifted' : ''}`} onContextMenu={(e) => e.preventDefault()} {...listeners}>
      <TaskCard {...props} />
      {/* the whole card drags by touch and mouse; this unseen button is the keyboard's way in */}
      <button type="button" ref={setActivatorNodeRef} className="plist__handle" aria-label={`Move ${item.task.title}`} {...attributes} />
    </div>
  )
}

/** The copy that follows the finger or mouse while dragging. */
export function DragGhost({ item, ctx }: { item: PlanItem; ctx: CardContext }) {
  const props = useCardProps(item, {}, ctx)
  return (
    <div className="plist__ghost">
      <TaskCard {...props} />
    </div>
  )
}
